/**
 * autocomplete_controller.js — address autocomplete via AddressAutocomplete (Stimulus).
 *
 * Bind to a CONTAINER that wraps the <input>:
 *
 *   <div data-controller="autocomplete"
 *        data-autocomplete-fields-value='{"street":"street","city":"city",...}'>
 *     <input id="address_input" ...>
 *   </div>
 *
 * AddressAutocomplete.createDropdown() moves the <input> into a generated wrapper
 * INSIDE this container, so the controller's own element never moves and cannot
 * loop (see VendorWidgetController for the full rationale). connect() instantiates
 * AddressAutocomplete on the inner <input>; disconnect() and a connect-scoped
 * turbo:before-cache both call instance.destroy().
 *
 * On selection the controller copies the suggestion's address components into
 * sibling hidden inputs. The mapping (suggestion key -> element id) is supplied on
 * the container via a JSON data attribute so one controller serves both the
 * create-client form (street/city/...) and the edit-client modal
 * (editStreet/editCity/...):
 *
 *   data-autocomplete-fields-value='{"street":"street","city":"city",...}'
 *
 * Clearing the input clears the mapped fields (skipped once right after a
 * selection, since the select itself fires an input event), preserving the
 * edit-field-clear behaviour. A reset of the input's form clears them too,
 * unless the input resets to a saved address (an edit form).
 */
import VendorWidgetController from "./_vendor_widget.js";

export default class extends VendorWidgetController {
        connect() {
            this._justSelected = false;
            this._fields = this._readFieldMap();
            this._resetInput = this.widgetElement();
            this._form = this._resetInput ? this._resetInput.form : null;
            this._onReset = () => this._handleReset();
            if (this._form) this._form.addEventListener('reset', this._onReset);
            super.connect();
        }

        disconnect() {
            if (this._form) this._form.removeEventListener('reset', this._onReset);
            super.disconnect();
        }

        vendorReady() { return typeof window.AddressAutocomplete !== 'undefined'; }

        widgetElement() { return this.element.querySelector('input'); }

        buildWidget(input) {
            this._inputEl = input;
            this._onInput = () => this._handleInput();
            input.addEventListener('input', this._onInput);
            var instance = new window.AddressAutocomplete(input, {
                onSelect: (suggestion) => this._handleSelect(suggestion),
            });
            // Park on the element under the same key address_autocomplete.js uses,
            // so the legacy frame-init's `!input.autocompleteInstance` check sees
            // this element as already wired and never double-binds.
            input.autocompleteInstance = instance;
            return instance;
        }

        destroyWidget(instance, input) {
            if (this._onInput && input) {
                input.removeEventListener('input', this._onInput);
            }
            if (instance && typeof instance.destroy === 'function') {
                try { instance.destroy(); } catch (_e) {}
            }
            if (input) input.autocompleteInstance = null;
        }

        _readFieldMap() {
            var raw = this.element.dataset.autocompleteFieldsValue;
            if (!raw) return {};
            try {
                return JSON.parse(raw);
            } catch (_e) {
                return {};
            }
        }

        _handleSelect(suggestion) {
            this._justSelected = true;
            Object.keys(this._fields).forEach((key) => {
                this._setField(this._fields[key], suggestion[key] || '');
            });
        }

        _handleInput() {
            if (this._justSelected) {
                this._justSelected = false;
                return;
            }
            if (!this._inputEl.value.trim()) this._clearFields();
        }

        // A hidden input's value is its default, so reset() leaves a picked
        // address's parts behind to post with the next save. Cleared at once,
        // before any handler that runs on the same reset reads the form. An
        // edit form's box resets to the saved address (its defaultValue), and
        // the saved parts stay with it.
        _handleReset() {
            if (this._resetInput.defaultValue) return;
            this._clearFields();
        }

        _clearFields() {
            Object.keys(this._fields).forEach((key) => {
                this._setField(this._fields[key], '');
            });
        }

        _setField(id, value) {
            var field = document.getElementById(id);
            if (field) field.value = value;
        }
}
